import { useToast } from "@/components/podkit/toasts/use-toast";
import { useGetPreferenceByKey, useSetPreference } from "@/queries/user-preferences-queries";
import { formatError } from "@/utils/errors";
import { useMutation } from "@tanstack/react-query";

export const PREFERRED_ENVIRONMENT_CLASS_PER_PROJECT = "PREFERRED_ENVIRONMENT_CLASS_ID_PER_PROJECT";

export function useGetPreferredEnvironmentClassPerProject() {
    const { data: preference, isPending } = useGetPreferenceByKey(PREFERRED_ENVIRONMENT_CLASS_PER_PROJECT);

    let preferredEnvironmentClassPerProject: Map<string, string> | undefined = new Map();
    const preferenceValue = preference?.value;
    if (preferenceValue) {
        try {
            const parsed = JSON.parse(preferenceValue) as [string, string][];
            preferredEnvironmentClassPerProject = new Map(parsed);
        } catch (e) {
            console.error(`Failed to parse ${PREFERRED_ENVIRONMENT_CLASS_PER_PROJECT} preference`, e);
        }
    }

    return {
        preferredEnvironmentClassPerProject,
        isPending,
    };
}

export function useSetPreferredEnvironmentClassIdPerProject() {
    const { toast } = useToast();
    const setPreference = useSetPreference();

    return useMutation({
        mutationFn: async (preferredEnvironmentClassPerProject: Map<string, string>) => {
            const maxSize = 1024 * 10; // 10KiB limit
            const entries: [string, string][] = [];

            // Try to add entries one by one until we hit the size limit
            for (const [projectId, environmentClassId] of preferredEnvironmentClassPerProject.entries()) {
                entries.push([projectId, environmentClassId]);
                const currentValue = JSON.stringify(entries);

                if (currentValue.length > maxSize) {
                    // Remove the last entry that caused us to exceed the limit
                    entries.pop();
                    break;
                }
            }

            const value = JSON.stringify(entries);
            return await setPreference.mutateAsync({ key: PREFERRED_ENVIRONMENT_CLASS_PER_PROJECT, value });
        },
        onError: (e) => {
            toast({
                title: "Failed to update preferred environment class",
                description: formatError(e),
            });
        },
    });
}
